import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  DialogBody,
  Button,
  RadioGroup,
  Radio,
  Input,
  Spinner,
  Switch,
  Dropdown,
  Option,
  Field,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  errorBox: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    borderRadius: '4px',
    padding: '12px',
  },
  subtleText: {
    color: tokens.colorNeutralForeground2,
  },
});
import {
  AddRegular,
  SearchRegular,
  PersonRegular,
  BuildingRegular,
  PersonAddRegular,
} from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addReturnToAppointment,
  searchDataverse,
  createDataverseContact,
  createDataverseAccount,
} from '../services/api';
import type {
  AddReturnToAppointmentRequest,
  TaxReturn,
  ContactInfo,
  AccountInfo,
} from '../types';
import { amendmentToggleLabel } from '../types';

interface AddReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  taxYear: number;
  parentReturnId?: string;
  onAdded: (taxReturn: TaxReturn) => void;
}

type DialogMode = 'search' | 'create';

export default function AddReturnDialog({
  open,
  onOpenChange,
  appointmentId,
  taxYear,
  parentReturnId,
  onAdded,
}: AddReturnDialogProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [returnType, setReturnType] = useState<'individual' | 'business'>('individual');
  const [isAmendment, setIsAmendment] = useState(false);
  const [mode, setMode] = useState<DialogMode>('search');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<{
    contacts: ContactInfo[];
    accounts: AccountInfo[];
  }>({ contacts: [], accounts: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  // Create form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: async (request: AddReturnToAppointmentRequest) => {
      return addReturnToAppointment(appointmentId, request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      queryClient.invalidateQueries({ queryKey: ['appointmentReturns', appointmentId] });
      queryClient.invalidateQueries({ queryKey: ['linkedReturns'] });
      onAdded(data);
      onOpenChange(false);
      resetForm();
    },
  });

  const resetForm = () => {
    setReturnType('individual');
    setIsAmendment(false);
    setMode('search');
    setSearchTerm('');
    setSearchResults({ contacts: [], accounts: [] });
    setHasSearched(false);
    setSearchError(null);
    setSelectedContactId('');
    setSelectedAccountId('');
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setEmail('');
    setPhone('');
    setCreateError(null);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    setHasSearched(false);
    setSearchError(null);
    try {
      const results = await searchDataverse(
        searchTerm,
        returnType === 'individual' ? 'contact' : 'account'
      );
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchError(error instanceof Error ? error.message : 'Search failed. Please try again.');
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCreateAndAdd = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      if (returnType === 'individual') {
        // Create contact
        const contact = await createDataverseContact({
          firstName,
          lastName,
          email: email || undefined,
          phone: phone || undefined,
        });

        // Add return with new contact
        const request: AddReturnToAppointmentRequest = {
          returnType,
          contactId: contact.contactId,
          parentReturnId,
          taxYear,
          isAmendment,
        };
        addMutation.mutate(request);
      } else {
        // Create account
        const account = await createDataverseAccount({
          name: companyName,
          email: email || undefined,
          phone: phone || undefined,
        });

        // Add return with new account
        const request: AddReturnToAppointmentRequest = {
          returnType,
          accountId: account.accountId,
          parentReturnId,
          taxYear,
          isAmendment,
        };
        addMutation.mutate(request);
      }
    } catch (error) {
      console.error('Create failed:', error);
      setCreateError(error instanceof Error ? error.message : 'Failed to create record');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAdd = () => {
    const request: AddReturnToAppointmentRequest = {
      returnType,
      contactId: returnType === 'individual' ? selectedContactId : undefined,
      accountId: returnType === 'business' ? selectedAccountId : undefined,
      parentReturnId,
      taxYear,
      isAmendment,
    };

    addMutation.mutate(request);
  };

  const canAddExisting = returnType === 'individual' ? selectedContactId : selectedAccountId;
  const canCreateNew = returnType === 'individual'
    ? (firstName.trim() && lastName.trim())
    : companyName.trim();

  const noResultsFound = hasSearched && !isSearching && (
    (returnType === 'individual' && searchResults.contacts.length === 0) ||
    (returnType === 'business' && searchResults.accounts.length === 0)
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        onOpenChange(data.open);
        if (!data.open) resetForm();
      }}
    >
      <DialogSurface style={{ maxWidth: '500px' }}>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AddRegular />
              Add Tax Return
            </div>
          </DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '20px' }}>
              <p className={styles.subtleText}>
                Add an additional tax return to this appointment.
              </p>
            </div>

            {/* Return type selection */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '8px' }}>Return Type:</h4>
              <RadioGroup
                value={returnType}
                onChange={(_, data) => {
                  setReturnType(data.value as 'individual' | 'business');
                  setSearchResults({ contacts: [], accounts: [] });
                  setHasSearched(false);
                  setSelectedContactId('');
                  setSelectedAccountId('');
                  setFirstName('');
                  setLastName('');
                  setCompanyName('');
                }}
              >
                <Radio
                  value="individual"
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PersonRegular />
                      <span>Individual (1040)</span>
                    </div>
                  }
                />
                <Radio
                  value="business"
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <BuildingRegular />
                      <span>Business</span>
                    </div>
                  }
                />
              </RadioGroup>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <Switch
                checked={isAmendment}
                onChange={(_, data) => setIsAmendment(data.checked)}
                label={amendmentToggleLabel(returnType)}
              />
            </div>

            {/* Mode selection */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  appearance={mode === 'search' ? 'primary' : 'secondary'}
                  icon={<SearchRegular />}
                  onClick={() => setMode('search')}
                  size="small"
                >
                  Search Existing
                </Button>
                <Button
                  appearance={mode === 'create' ? 'primary' : 'secondary'}
                  icon={<PersonAddRegular />}
                  onClick={() => setMode('create')}
                  size="small"
                >
                  Create New
                </Button>
              </div>
            </div>

            {/* Search Mode */}
            {mode === 'search' && (
              <>
                {/* Search */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '8px' }}>
                    Search {returnType === 'individual' ? 'Contacts' : 'Accounts'}:
                  </h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      placeholder={`Search ${returnType === 'individual' ? 'contacts' : 'accounts'}...`}
                      value={searchTerm}
                      onChange={(_, data) => setSearchTerm(data.value)}
                      onKeyDown={handleKeyDown}
                      contentBefore={<SearchRegular />}
                      style={{ flex: 1 }}
                    />
                    <Button
                      appearance="primary"
                      onClick={handleSearch}
                      disabled={isSearching || !searchTerm.trim()}
                    >
                      {isSearching ? <Spinner size="tiny" /> : 'Search'}
                    </Button>
                  </div>
                </div>

                {/* Search error */}
                {searchError && (
                  <div className={styles.errorBox} style={{ marginBottom: '16px' }}>
                    {searchError}
                  </div>
                )}

                {/* Search results */}
                {returnType === 'individual' && searchResults.contacts.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '8px' }}>Select Contact:</h4>
                    <Dropdown
                      value={searchResults.contacts.find(c => c.contactId === selectedContactId)?.fullName || ''}
                      onOptionSelect={(_, data) => setSelectedContactId(data.optionValue || '')}
                      style={{ width: '100%' }}
                      placeholder="Select a contact..."
                    >
                      {searchResults.contacts.map(contact => (
                        <Option
                          key={contact.contactId}
                          value={contact.contactId}
                          text={contact.fullName}
                        >
                          {contact.fullName}{contact.email ? ` (${contact.email})` : ''}
                        </Option>
                      ))}
                    </Dropdown>
                  </div>
                )}

                {returnType === 'business' && searchResults.accounts.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '8px' }}>Select Account:</h4>
                    <Dropdown
                      value={searchResults.accounts.find(a => a.accountId === selectedAccountId)?.name || ''}
                      onOptionSelect={(_, data) => setSelectedAccountId(data.optionValue || '')}
                      style={{ width: '100%' }}
                      placeholder="Select an account..."
                    >
                      {searchResults.accounts.map(account => (
                        <Option
                          key={account.accountId}
                          value={account.accountId}
                          text={account.name}
                        >
                          {account.name}{account.primaryContactName ? ` - ${account.primaryContactName}` : ''}
                        </Option>
                      ))}
                    </Dropdown>
                  </div>
                )}

                {/* No results message with create option */}
                {noResultsFound && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--tl-bg-subtle)',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}>
                    <p className={styles.subtleText} style={{ marginBottom: '12px' }}>
                      No {returnType === 'individual' ? 'contacts' : 'accounts'} found for "{searchTerm}".
                    </p>
                    <Button
                      appearance="primary"
                      icon={<PersonAddRegular />}
                      onClick={() => {
                        setMode('create');
                        // Pre-fill name from search term
                        if (returnType === 'individual') {
                          const parts = searchTerm.trim().split(' ');
                          if (parts.length >= 2) {
                            setFirstName(parts[0]);
                            setLastName(parts.slice(1).join(' '));
                          } else {
                            setLastName(searchTerm.trim());
                          }
                        } else {
                          setCompanyName(searchTerm.trim());
                        }
                      }}
                    >
                      Create New {returnType === 'individual' ? 'Contact' : 'Account'}
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Create Mode */}
            {mode === 'create' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {returnType === 'individual' ? (
                  <>
                    <Field label="First Name" required>
                      <Input
                        value={firstName}
                        onChange={(_, data) => setFirstName(data.value)}
                        placeholder="Enter first name"
                      />
                    </Field>
                    <Field label="Last Name" required>
                      <Input
                        value={lastName}
                        onChange={(_, data) => setLastName(data.value)}
                        placeholder="Enter last name"
                      />
                    </Field>
                  </>
                ) : (
                  <Field label="Company Name" required>
                    <Input
                      value={companyName}
                      onChange={(_, data) => setCompanyName(data.value)}
                      placeholder="Enter company name"
                    />
                  </Field>
                )}
                <Field label="Email">
                  <Input
                    type="email"
                    value={email}
                    onChange={(_, data) => setEmail(data.value)}
                    placeholder="Enter email address"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(_, data) => setPhone(data.value)}
                    placeholder="Enter phone number"
                  />
                </Field>

                {createError && (
                  <div className={styles.errorBox}>
                    {createError}
                  </div>
                )}
              </div>
            )}

            {/* Error display */}
            {addMutation.isError && (
              <div className={styles.errorBox} style={{ marginTop: '16px' }}>
                {addMutation.error?.message || 'Failed to add return'}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            {mode === 'search' ? (
              <Button
                appearance="primary"
                onClick={handleAdd}
                disabled={addMutation.isPending || !canAddExisting}
              >
                {addMutation.isPending ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: '8px' }} />
                    Adding...
                  </>
                ) : (
                  'Add Return'
                )}
              </Button>
            ) : (
              <Button
                appearance="primary"
                onClick={handleCreateAndAdd}
                disabled={isCreating || addMutation.isPending || !canCreateNew}
              >
                {isCreating || addMutation.isPending ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: '8px' }} />
                    Creating...
                  </>
                ) : (
                  'Create & Add Return'
                )}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
